import React, { useState, useEffect } from 'react';
import { getSearchListings, markListingsAsRead } from '../lib/firebaseService';
import ListingCard from './ListingCard';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ArrowLeft,
  Filter,
  SortAsc,
  SortDesc,
  Calendar,
  DollarSign,
  Eye,
  RefreshCw,
  ExternalLink,
  Grid3X3,
  List
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const SearchDetails = ({ search, onBack, onRefreshSearch }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [sortBy, setSortBy] = useState('datePosted');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (search) {
      loadListings();
    }
  }, [search, showOnlyNew]);

  const loadListings = async () => {
    try {
      setLoading(true);
      const result = await getSearchListings(search.id, showOnlyNew);
      if (result.success) {
        setListings(result.listings);
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger les annonces.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading listings:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement des annonces.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async () => {
    try {
      const result = await markListingsAsRead(search.id);
      if (result.success) {
        // Update local state to mark all listings as read
        setListings(listings.map(listing => ({ ...listing, isNew: false })));
        toast({
          title: "Succès",
          description: "Toutes les annonces ont été marquées comme vues.",
        });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour.",
        variant: "destructive",
      });
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      if (onRefreshSearch) {
        await onRefreshSearch(search.id);
        await loadListings(); // Reload listings after refresh
      }
    } catch (error) {
      console.error('Error refreshing search:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const sortedListings = React.useMemo(() => {
    const sorted = [...listings].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'year':
          aValue = a.year || 0;
          bValue = b.year || 0;
          break;
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'datePosted':
          aValue = new Date(a.datePosted || 0);
          bValue = new Date(b.datePosted || 0);
          break;
        case 'firstSeen':
        default:
          aValue = new Date(a.firstSeen || 0);
          bValue = new Date(b.firstSeen || 0);
          break;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return sorted;
  }, [listings, sortBy, sortOrder]);

  const newListingsCount = listings.filter(listing => listing.isNew).length;

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date inconnue';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Date inconnue';
    }
  };

  const extractSearchInfo = (url) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      return {
        location: urlObj.pathname.includes('casablanca') ? 'Casablanca' : 'Maroc',
        brand: params.get('brand') || 'Toutes marques',
        model: params.get('model') || 'Tous modèles',
        fuel: params.get('fuel') || null,
        yearRange: params.get('regdate') || null
      };
    } catch {
      return {
        location: 'Maroc',
        brand: 'Toutes marques',
        model: 'Tous modèles'
      };
    }
  };

  const searchInfo = extractSearchInfo(search.url);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>

        {/* Listings Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 h-64 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour aux recherches</span>
          </Button>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(search.url, '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Voir sur Avito
            </Button>

            {newListingsCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAsRead}
              >
                <Eye className="w-4 h-4 mr-1" />
                Marquer comme vu
              </Button>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{search.name}</h1>
            {newListingsCount > 0 && (
              <Badge variant="destructive">
                {newListingsCount} nouveau{newListingsCount > 1 ? 'x' : ''}
              </Badge>
            )}
          </div>

          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>📍 {searchInfo.location}</span>
            <span>🚗 {searchInfo.brand}</span>
            {searchInfo.model !== 'Tous modèles' && <span>• {searchInfo.model}</span>}
            <span>📅 Dernière vérification: {formatDate(search.lastScraped)}</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            {/* Filter by new */}
            <Button
              variant={showOnlyNew ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlyNew(!showOnlyNew)}
            >
              <Filter className="w-4 h-4 mr-1" />
              {showOnlyNew ? 'Toutes' : 'Nouvelles uniquement'}
            </Button>

            {/* Sort options */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1 border rounded text-sm"
            >
              <option value="datePosted">Date de publication</option>
              <option value="firstSeen">Date d'ajout</option>
              <option value="price">Prix</option>
              <option value="year">Année</option>
              <option value="title">Titre</option>
            </select>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {sortedListings.length} annonce{sortedListings.length > 1 ? 's' : ''}
              {showOnlyNew && newListingsCount > 0 && ` nouvelle${newListingsCount > 1 ? 's' : ''}`}
            </span>

            {/* View mode toggle */}
            <div className="flex border rounded">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Listings */}
      {sortedListings.length > 0 ? (
        <div className={
          viewMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            : "space-y-4"
        }>
          {sortedListings.map((listing) => (
            <div key={listing.id} className="relative">
              <ListingCard
                listing={{
                  id: listing.annonceId || listing.id,
                  sellerName: listing.sellerName || 'Vendeur',
                  isPremium: listing.isPremium || false,
                  isNew: listing.isNew || false,
                  title: listing.title,
                  price: listing.price,
                  monthlyPayment: listing.monthlyPayment,
                  year: listing.year,
                  model: listing.model,
                  transmission: listing.transmission || 'N/A',
                  fuel: listing.fuel || 'N/A',
                  location: listing.location,
                  image: listing.image,
                  imageCount: listing.imageCount || 1,
                  url: listing.url,
                  datePosted: listing.datePosted,
                  dateAdded: listing.firstSeen
                }}
                viewMode={viewMode}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border">
          <div className="text-gray-400 mb-4">
            <Calendar className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {showOnlyNew ? 'Aucune nouvelle annonce' : 'Aucune annonce trouvée'}
          </h3>
          <p className="text-gray-600 mb-4">
            {showOnlyNew
              ? 'Toutes les annonces ont été vues. Actualisez la recherche pour vérifier de nouvelles annonces.'
              : 'Cette recherche ne contient aucune annonce pour le moment.'}
          </p>
          {showOnlyNew && (
            <Button variant="outline" onClick={() => setShowOnlyNew(false)}>
              Voir toutes les annonces
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchDetails;